import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent } from "@/components/ui/card";

const PrivacyPolicy = () => {
  return (
    <PageContainer>
      <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
      
      <Card className="mb-8">
        <CardContent className="p-6">
          <div className="prose max-w-none">
            <p className="text-gray-500 mb-6">Last updated: May 4, 2025</p>
            
            <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
            <p>
              PokeScan ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, mobile application, and services (collectively, the "Services").
            </p>
            <p className="mt-3">
              We are a small startup, and therefore we have implemented these rules to protect both you and our fledgling company. By using our Services, you acknowledge that we may have limited resources and that these policies are designed to define the risks and responsibilities for all parties.
            </p>
            
            <h2 className="text-xl font-semibold mb-3 mt-6">2. Information We Collect</h2>
            
            <h3 className="text-lg font-medium mb-2">2.1 Personal Data</h3>
            <p>
              When you use our Services, we may collect information that identifies you, including:
            </p>
            <ul className="list-disc pl-6 mb-3">
              <li>Contact information (such as email address)</li>
              <li>Account information (such as username and password)</li>
              <li>Payment information (through our payment processor, Stripe)</li>
              <li>Profile information (such as display name)</li>
            </ul>
            
            <h3 className="text-lg font-medium mb-2">2.2 Card Images</h3>
            <p>
              When you use our card scanning feature, we temporarily process the images you upload to identify the card. These images may be stored for up to 24 hours for processing and then deleted, unless you explicitly save them to your collection.
            </p>
            
            <h3 className="text-lg font-medium mb-2">2.3 Usage Data</h3>
            <p>
              We automatically collect certain information about your device and how you interact with our Services, including:
            </p>
            <ul className="list-disc pl-6 mb-3">
              <li>IP address</li>
              <li>Device type and operating system</li>
              <li>Browser type</li>
              <li>Pages viewed and actions taken within the Services</li>
              <li>Time and date of your visit</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">2.4 Legal Basis (EU/EEA Residents)</h3>
            <p>
              If you are located in the European Union or European Economic Area, we process your personal data only when we have a lawful basis, including:
            </p>
            <ul className="list-disc pl-6 mb-3">
              <li>Performance of contract: to provide the Services you request.</li>
              <li>Legal compliance: to comply with obligations such as tax or fraud prevention.</li>
              <li>Legitimate interests: to improve and secure our Services, provided your rights are not overridden.</li>
              <li>Consent: for optional features like marketing emails, which you can withdraw at any time.</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">2.5 Data Retention</h3>
            <p>We retain personal data for as long as necessary to fulfill the purposes below, plus any retention period required by law:</p>
            <ul className="list-disc pl-6 mb-3">
              <li>Account & profile data: active + 2 years</li>
              <li>Payment & billing data: 7 years</li>
              <li>Usage logs & analytics: 12 months</li>
              <li>Uploaded card images: 24 hours (unless saved)</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">2.6 Cookies & Tracking Technologies</h3>
            <p>
              We use cookies, web beacons, and similar technologies (e.g., Google Analytics) to:
            </p>
            <ul className="list-disc pl-6 mb-3">
              <li>Remember your login session.</li>
              <li>Analyze usage and performance.</li>
              <li>Provide personalized content.</li>
            </ul>
            <p>
              You can manage or disable cookies in your browser settings, but this may affect site functionality.
            </p>

            <h3 className="text-lg font-medium mb-2">2.7 Third-Party Processors & International Transfers</h3>
            <p>
              We share data with service providers under strict contracts.
            </p>
            <p>
              Some processors are located outside the EEA. We rely on EU Standard Contractual Clauses and other safeguards to ensure adequate protection of your data.
            </p>
            
            <h2 className="text-xl font-semibold mb-3 mt-6">3. How We Use Your Information</h2>
            <p>
              We use the information we collect for various purposes, including:
            </p>
            <ul className="list-disc pl-6 mb-3">
              <li>Providing, maintaining, and improving our Services</li>
              <li>Processing subscriptions and transactions</li>
              <li>Responding to your requests and inquiries</li>
              <li>Sending you technical notices, updates, and support messages</li>
              <li>Detecting, preventing, and addressing fraud, security, or technical issues</li>
              <li>Analyzing usage patterns to improve the user experience</li>
            </ul>
            
            <h2 className="text-xl font-semibold mb-3 mt-6">4. Disclosure of Your Information</h2>
            <p>
              We may share your information with:
            </p>
            <ul className="list-disc pl-6 mb-3">
              <li>Service providers who perform services on our behalf (such as Stripe for payment processing)</li>
              <li>Third-party APIs necessary for card identification and valuation</li>
              <li>Law enforcement or other governmental officials, if required by law</li>
            </ul>
            
            <h2 className="text-xl font-semibold mb-3 mt-6">5. Data Security</h2>
            <p>
              We implement reasonable security measures to protect your personal information from unauthorized access, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
            
            <h2 className="text-xl font-semibold mb-3 mt-6">6. Your Rights and Choices</h2>
            <p>
              Depending on your location, you may have rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-6 mb-3">
              <li>Access to your personal data</li>
              <li>Correction of inaccurate data</li>
              <li>Deletion of your personal data</li>
              <li>Restriction of processing of your data</li>
              <li>Data portability</li>
              <li>Objection to processing</li>
            </ul>
            <p>
              To exercise these rights, please contact us at email: anders.erikstad@outlook.com.
            </p>
            <p className="mt-3 font-semibold">
              <strong>California Residents:</strong> You have the right to request disclosure of the categories of personal data we collect, deletion of your personal data, and to opt-out of any sale of personal data. We do not sell personal data, but you may opt-out of sharing for targeted advertising.
            </p>
            <p className="mt-3 font-semibold">
              <strong>EU/EEA Residents:</strong> In addition to the rights above, you may withdraw consent at any time, lodge a complaint with your local supervisory authority, and our lawful bases for processing are detailed in Section 2.4.
            </p>
            
            <h2 className="text-xl font-semibold mb-3 mt-6">7. Children's Privacy</h2>
            <p>
              Our Services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
            </p>
            
            <h2 className="text-xl font-semibold mb-3 mt-6">8. Changes to This Privacy Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
            
            <h2 className="text-xl font-semibold mb-3 mt-6">9. Data Protection Officer</h2>
            <p>
              If you are an EU/EEA resident, you may contact our Data Protection Officer at:<br/>
              Email: anders.erikstad@outlook.com<br/>
              Address: Fantoftvegen 14E, Bergen, Norway
            </p>
            
            <h2 className="text-xl font-semibold mb-3 mt-6">10. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us at:<br/>
              Email: anders.erikstad@outlook.com<br/>
              Address: Fantoftvegen 14E, Bergen, Norway
            </p>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default PrivacyPolicy;
